import React, { useState, createContext, useContext } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";

const TranslationContext = createContext();

const translations = {
  en: {
    search: "Search",
    searchPlaceholder: "Search...",
    switchLanguage: "Switch Language",
    live: "LIVE",
    account: "Account",
    logout: "Logout",
    signIn: "Sign In",
    signUp: "Sign Up",
    loading: "Loading...",
    error: "Error occurred",
    home: "Home",
    about: "About",
    contact: "Contact",
    settings: "Settings",
    heroDescription: "Your Source For Live Digital Broadcasts. Powered By",
    homeInstruction: "Select a thumbnail below to watch digital broadcasts",
    footerPowered: "Powered by",
    footerWebsite: "Website",
    footerWebsite2: "Previous Archives",
    footerAdmin: "Admin",
    shortDescription: "Short description:",
    noShortDescripton: "No short description available",
    longDescription: "Long description:",
    noLongDescription: "No long description available",
    videos: "Videos",
    notFound: "404 - Page Not Found",
    notFoundSorry: `Sorry, the page you're looking for doesn't exist.`,
    notFoundGoHome: "Go Home",
    noVideosAvailable: "No Videos Available",
    previousVideosPrompt: "Looking for previous videos?",
    clickHere: "Click Here",
    accessOldWebsite: "to access the old website.",
    // Login Page
    loginTitle: "Sign In",
    loginError: "Invalid Username or Password",
    email: "Email",
    password: "Password",
    rememberMe: "Remember Me",
    resetPassword: "Reset Password",
    newToRingetteLive: "New to Ringette Live?",
    signingInNote:
      "*Signing in is not required and is used for saving series, password access and helps us know how many people use the site",
    // Signup Page
    signupTitle: "Sign Up",
    emailAlreadyInUse: "Email already in use",
    failedToCreateAccount: "Failed to create an account",
    alreadySubscribed: "Already subscribed to Ringette Live?",
    // Forgot Password Page
    sendResetEmail: "Send Password Reset Email",
    rememberedPasswordQuestion: "Remembered your password?",
    checkInbox: "Check your inbox for further instructions",
    // Forbidden Page
    forbiddenTitle: "Forbidden: Access Denied",
    yourUidIs: "Your UID is:",
  },
  fr: {
    search: "Rechercher",
    searchPlaceholder: "Rechercher...",
    switchLanguage: "Changer de langue",
    live: "EN DIRECT",
    account: "Compte",
    logout: "Déconnexion",
    signIn: "Se connecter",
    signUp: "S'inscrire",
    loading: "Chargement...",
    error: "Une erreur est survenue",
    home: "Accueil",
    about: "À propos",
    contact: "Contact",
    settings: "Paramètres",
    heroDescription:
      "Votre source pour les diffusions numériques en direct. Propulsé par",
    homeInstruction:
      "Sélectionnez une miniature ci-dessous pour regarder les diffusions numériques",
    footerPowered: "Propulsé par",
    footerWebsite: "Site Web",
    footerWebsite2: "Archives précédentes",
    footerAdmin: "Admin",
    shortDescription: "Description courte:",
    noShortDescripton: "Aucune courte description disponible",
    longDescription: "Description longue:",
    noLongDescription: "Aucune longue description disponible",
    videos: "Vidéos",
    notFound: "404 - Page non trouvée",
    notFoundSorry: `Désolé, la page que vous recherchez n'existe pas.`,
    notFoundGoHome: "Retour à l'accueil",
    noVideosAvailable: "Aucune vidéo disponible",
    previousVideosPrompt: "Vous cherchez des vidéos précédentes ?",
    clickHere: "Cliquez ici",
    accessOldWebsite: "pour accéder à l'ancien site.",
    // Login Page
    loginTitle: "Se connecter",
    loginError: "Nom d'utilisateur ou mot de passe invalide",
    email: "Courriel",
    password: "Mot de passe",
    rememberMe: "Se souvenir de moi",
    resetPassword: "Réinitialiser le mot de passe",
    newToRingetteLive: "Nouveau sur Ringette Live?",
    signingInNote:
      "*La connexion n'est pas requise et est utilisée pour sauvegarder les séries, l'accès par mot de passe et nous aide à savoir combien de personnes utilisent le site",
    // Signup Page
    signupTitle: "S'inscrire",
    emailAlreadyInUse: "Courriel déjà utilisé",
    failedToCreateAccount: "Échec de la création d'un compte",
    alreadySubscribed: "Déjà abonné à Ringette Live?",
    // Forgot Password Page
    sendResetEmail: "Envoyer l’e-mail de réinitialisation",
    rememberedPasswordQuestion: "Vous vous souvenez de votre mot de passe ?",
    checkInbox:
      "Vérifiez votre boîte de réception pour la suite des instructions",
    // Forbidden Page
    forbiddenTitle: "Accès interdit : accès refusé",
    yourUidIs: "Votre UID :",
  },
};

export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem("language") || "en";
  });

  const setLanguage = (lang) => {
    setCurrentLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const t = (key) => translations[currentLanguage][key] || key;

  const functions = getFunctions(app, "us-central1");
  const translateOnceFn = httpsCallable(functions, "translateOnce");

  const translateText = async (text, targetLang) => {
    if (!text || targetLang === "en") return text;
    try {
      const res = await translateOnceFn({ text, to: targetLang, source: "en" });
      return res?.data ?? text;
    } catch (e) {
      console.error("translateOnce failed:", e);
      return text;
    }
  };

  const translateBatch = async (arr, targetLang) => {
    if (!Array.isArray(arr) || !arr.length) return arr;
    try {
      const res = await translateOnceFn({
        text: arr,
        to: targetLang,
        source: "en",
      });
      return Array.isArray(res?.data) ? res.data : arr;
    } catch {
      return arr;
    }
  };

  const value = {
    currentLanguage,
    setLanguage,
    t,
    translateText,
    translateBatch,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};
